import {create} from "zustand";

/**
 * Represents an individual breadcrumb item.
 */
export interface BreadcrumbItem {
  path: string;
  title: string;
  disabledPath?: boolean;
}

/**
 * Basic information model.
 */
interface BaseInfo {
  name?: string;
  id: number;
}

/**
 * Full breadcrumb state structure with optional context info.
 */
export interface Breadcrumb {
  items: BreadcrumbItem[];
  client?: BaseInfo;
  building?: BaseInfo;
  plant?: BaseInfo;
  product?: BaseInfo;
}

/**
 * Zustand store state for managing breadcrumb navigation.
 */
interface BreadcrumbStoreState {
  items: BreadcrumbItem[];
  client: BaseInfo;
  building: BaseInfo;
  plant: BaseInfo;
  product: BaseInfo;
}

/**
 * Actions available to manipulate breadcrumb state.
 */
type BreadcrumbStoreActions = {
  updateBreadcrumb: (newBreadcrumb: BreadcrumbItem[]) => void;
  removeLastBreadcrumb: () => void;
  resetBreadcrumb: () => void;
};

/**
 * Zustand store for managing breadcrumb navigation state across the application.
 *
 * @example
 * ```tsx
 * const { items, updateBreadcrumb, removeLastBreadcrumb, resetBreadcrumb } = useBreadcrumbStore();
 * 
 * updateBreadcrumb([{ path: '/dashboard', title: 'Dashboard' }]);
 * removeLastBreadcrumb();
 * resetBreadcrumb();
 * ```
 */
const useBreadcrumbStore = create<
  BreadcrumbStoreState & BreadcrumbStoreActions
>((set) => ({
  items: [],
  client: { id: 0, name: "" },
  building: { id: 0, name: "" },
  plant: { id: 0, name: "" },
  product: { id: 0, name: "" },
  updateBreadcrumb: (newBreadcrumb) => set({ items: newBreadcrumb }),
  removeLastBreadcrumb: () =>
    set((state) => ({ items: state.items.slice(0, -1) })),
  resetBreadcrumb: () => set({ items: [] }),
}));

export default useBreadcrumbStore;
